import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useNotifications } from "@/hooks/useNotifications";

export interface PaymentReceipt {
  id: string;
  user_id: string;
  gym_id: string;
  category_id: string | null;
  category_name: string | null;
  receipt_url: string;
  status: string;
  rejection_reason: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  created_at: string;
}

export const useUserReceipts = (userId: string | undefined) => {
  return useQuery({
    queryKey: ["payment-receipts", "user", userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("payment_receipts")
        .select("*")
        .eq("user_id", userId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as PaymentReceipt[];
    },
    enabled: !!userId,
  });
};

export const useGymPendingReceipts = (gymId: string | null) => {
  return useQuery({
    queryKey: ["payment-receipts", "gym", gymId, "pending"],
    queryFn: async () => {
      const { data: receipts, error } = await supabase
        .from("payment_receipts")
        .select("*")
        .eq("gym_id", gymId!)
        .eq("status", "pending")
        .order("created_at", { ascending: false });
      if (error) throw error;

      if (!receipts?.length) return [];

      const userIds = [...new Set(receipts.map((r) => r.user_id))];

      const { data: profiles } = await supabase
        .from("profiles")
        .select("id, first_name, last_name, dni, username")
        .in("id", userIds);

      const { data: pending } = await supabase
        .from("pending_members")
        .select("id, first_name, last_name, dni")
        .in("id", userIds);

      const profilesMap = new Map(profiles?.map((p) => [p.id, p]) || []);
      const pendingMap = new Map(pending?.map((p) => [p.id, p]) || []);

      return receipts.map((receipt) => {
        const profile = profilesMap.get(receipt.user_id);
        const pendingMember = pendingMap.get(receipt.user_id);
        const userData = profile || pendingMember;
        return {
          ...receipt,
          user_first_name: userData?.first_name || null,
          user_last_name: userData?.last_name || null,
          user_dni: userData?.dni || null,
          user_username: profile?.username || null,
        };
      });
    },
    enabled: !!gymId,
  });
};

export const useCreateReceipt = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  return useMutation({
    mutationFn: async (data: {
      user_id: string;
      gym_id: string;
      category_id: string | null;
      category_name: string | null;
      receipt_url: string;
    }) => {
      const { error } = await supabase.from("payment_receipts").insert(data);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["payment-receipts"] });
      success("Comprobante enviado", { description: "Tu comprobante fue enviado y está pendiente de revisión" });
    },
    onError: () => {
      showError("No se pudo enviar el comprobante");
    },
  });
};

export const useUpdateReceiptStatus = () => {
  const queryClient = useQueryClient();
  const { success, error: showError } = useNotifications();

  return useMutation({
    mutationFn: async (data: {
      id: string;
      status: "approved" | "rejected";
      rejection_reason?: string;
      reviewed_by: string;
    }) => {
      const { error } = await supabase
        .from("payment_receipts")
        .update({
          status: data.status,
          rejection_reason: data.rejection_reason || null,
          reviewed_by: data.reviewed_by,
          reviewed_at: new Date().toISOString(),
        })
        .eq("id", data.id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["payment-receipts"] });
      if (variables.status === "approved") {
        success("Comprobante aprobado");
      } else {
        success("Comprobante rechazado");
      }
    },
    onError: () => {
      showError("No se pudo actualizar el comprobante");
    },
  });
};
